import React, { useRef, useEffect } from 'react';

/**
 * OTP Input Component
 * A 6-digit OTP input with auto-focus and paste support
 */
const OTPInput = ({ value = '', onChange, disabled = false, error = false }) => {
  const inputRefs = useRef([]);
  const length = 6;

  // Focus first empty input on mount
  useEffect(() => {
    const firstEmpty = value.length;
    if (firstEmpty < length && inputRefs.current[firstEmpty]) {
      inputRefs.current[firstEmpty].focus();
    }
  }, []);

  const handleChange = (index, e) => {
    const digit = e.target.value;

    // Only allow single digit
    if (!/^\d?$/.test(digit)) return;

    const newValue = value.split('');
    newValue[index] = digit;
    const newOtp = newValue.join('').slice(0, length);

    onChange(newOtp);

    // Auto-focus next input
    if (digit && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace') {
      if (!value[index] && index > 0) {
        // If current input is empty, move to previous
        inputRefs.current[index - 1]?.focus();
      }
    }

    // Handle arrow keys
    if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowRight' && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    const digits = pastedData.replace(/\D/g, '').slice(0, length);

    if (digits) {
      onChange(digits);
      // Focus the next empty input or the last one
      const focusIndex = Math.min(digits.length, length - 1);
      inputRefs.current[focusIndex]?.focus();
    }
  };

  const handleFocus = (e) => {
    e.target.select();
  };

  return (
    <div className="otp-input-container">
      <div className="otp-inputs">
        {Array.from({ length }, (_, index) => (
          <input
            key={index}
            ref={(el) => (inputRefs.current[index] = el)}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={value[index] || ''}
            onChange={(e) => handleChange(index, e)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={handlePaste}
            onFocus={handleFocus}
            disabled={disabled}
            className={`otp-digit ${error ? 'error' : ''} ${value[index] ? 'filled' : ''}`}
            autoComplete="one-time-code"
          />
        ))}
      </div>
      <style>{`
        .otp-input-container {
          width: 100%;
        }

        .otp-inputs {
          display: flex;
          justify-content: center;
          gap: clamp(4px, 2vw, 12px);
        }

        .otp-digit {
          width: clamp(36px, 10vw, 52px);
          height: clamp(44px, 12vw, 60px);
          text-align: center;
          font-size: clamp(18px, 5vw, 24px);
          font-weight: 600;
          border: 2px solid #e0e0e0;
          border-radius: clamp(8px, 2vw, 12px);
          background: #fff;
          transition: all 0.2s ease;
          outline: none;
        }

        .otp-digit:focus {
          border-color: #FF6961;
          box-shadow: 0 0 0 3px rgba(255, 105, 97, 0.1);
        }

        .otp-digit.filled {
          border-color: #FF6961;
          background: #fef7f7;
        }

        .otp-digit.error {
          border-color: #dc2626;
          background: #fef2f2;
        }

        .otp-digit:disabled {
          background: #f5f5f5;
          cursor: not-allowed;
        }

        /* Tablet */
        @media (max-width: 768px) {
          .otp-inputs {
            gap: 8px;
          }
          .otp-digit {
            width: 44px;
            height: 52px;
            font-size: 22px;
          }
        }

        /* Mobile */
        @media (max-width: 480px) {
          .otp-inputs {
            gap: 6px;
          }
          .otp-digit {
            width: 40px;
            height: 48px;
            font-size: 20px;
          }
        }

        /* Small Mobile */
        @media (max-width: 360px) {
          .otp-inputs {
            gap: 4px;
          }
          .otp-digit {
            width: 36px;
            height: 44px;
            font-size: 18px;
            border-radius: 8px;
          }
        }
      `}</style>
    </div>
  );
};

export default OTPInput;
